import { useState } from "react";
import axios from "axios";
import OnboardingShell from "./components/OnboardingShell";
import { API_BASE_URL } from "./config";
import { UploadCloud } from "lucide-react";

// Keys written across Step 1 and Step 2 that must all be cleared
// once onboarding is actually submitted — previously only
// step1Data/step2Data were cleared, leaving stale values to
// silently pre-fill a brand-new onboarding attempt.
const ALL_ONBOARDING_KEYS = [
  "step1Data",
  "step2Data",
  "signature",
  "selectedProducts",
  "subscription",
  "goLiveDate",
];

function Step4PaymentProof({ onNext, onBack }) {

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!file) {
      setError("Please upload payment proof");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const step1 = JSON.parse(localStorage.getItem("step1Data"));
      const step2 = JSON.parse(localStorage.getItem("step2Data"));

      if (!step1 || !step2) {
        setError("Onboarding data missing. Please restart.");
        setLoading(false);
        return;
      }

      const selectedProducts = step2.selectedProducts || {};
      const productNames = Object.keys(selectedProducts).join(", ");
      const totalCredits = Object.values(selectedProducts).reduce((acc, val) => acc + Number(val || 0), 0);

      const PRICE_MAP = {
        videosage: 50, audiosage: 30, screensage: 40, codesage: 60,
        aibuddy_basic: 4500, aibuddy_premium: 6500
      };
      const totalAmount = Object.keys(selectedProducts).reduce((acc, key) => {
        const qty = Number(selectedProducts[key]) || 0;
        return acc + (qty * (PRICE_MAP[key] || 0));
      }, 0);

      const formData = new FormData();
      formData.append("company", JSON.stringify(step1));
      formData.append(
        "purchase",
        JSON.stringify({
          productName: productNames,
          planType: "", // Removed from frontend
          credits: totalCredits,
          subscriptionType: step2.subscription,
          addOns: "", // Removed from frontend
          expectedGoLiveDate: step2.goLiveDate,
          totalAmount: totalAmount
        })
      );
      formData.append("file", file);

      await axios.post(
        `${API_BASE_URL}/api/onboarding/complete`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      localStorage.setItem("finalStep1Data", JSON.stringify(step1));
      localStorage.setItem("finalStep2Data", JSON.stringify(step2));

      ALL_ONBOARDING_KEYS.forEach((key) => localStorage.removeItem(key));

      onNext();
    } catch (err) {
      console.error(err);
      setError("Failed to submit onboarding. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingShell
      step={4}
      title="Upload payment proof"
      subtitle="Upload your payment receipt or screenshot to continue."
    >
      {error && <p className="onboard-error" style={{ marginBottom: 14 }}>{error}</p>}

      <label className="upload-zone">
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={(e) => { setFile(e.target.files[0]); setError(""); }}
        />
        <div className="upload-zone-icon-wrap">
          <UploadCloud size={32} color="#8B5CF6" />
        </div>
        <div className="upload-zone-text">
          <span className="upload-title">Click to upload or drag and drop</span><br />
          <span className="upload-subtitle">SVG, PNG, JPG or PDF (max. 10MB)</span>
        </div>
        {file && <div className="upload-zone-file">{file.name}</div>}
      </label>

      <div className="button-row">
        <button className="onboard-button secondary" onClick={onBack} disabled={loading} type="button">
          Back
        </button>
        <button className="onboard-button onboard-button--primary" onClick={handleSubmit} disabled={loading} type="button">
          {loading ? "Submitting…" : "Submit & continue"}
        </button>
      </div>
    </OnboardingShell>
  );
}

export default Step4PaymentProof;